import { useEffect, useState } from "react";
import { Shuffle, Bookmark } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { regenerateHandle } from "@/lib/user";
import VentCard from "@/components/VentCard";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

export default function Profile({ user, setUser }) {
  const [posts, setPosts] = useState([]);
  const [bookmarks, setBookmarks] = useState([]);
  const [regenerating, setRegenerating] = useState(false);

  const load = async () => {
    const [p, b] = await Promise.all([
      api.get(`/users/${user.id}/posts`),
      api.get(`/users/${user.id}/bookmarks`),
    ]);
    setPosts(p.data);
    setBookmarks(b.data);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [user.handle]);

  const shuffleHandle = async () => {
    setRegenerating(true);
    try {
      const updated = await regenerateHandle(user.id);
      setUser(updated);
      toast.success(`New handle: ${updated.handle}`);
    } finally {
      setRegenerating(false);
    }
  };

  const updatePost = (u) => setPosts((prev) => prev.map((p) => (p.id === u.id ? u : p)));
  const updateBookmark = (u) => {
    setBookmarks((prev) => {
      if (!u.bookmarked) return prev.filter((p) => p.id !== u.id);
      return prev.map((p) => (p.id === u.id ? u : p));
    });
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div
        className="rounded-2xl border p-6 sm:p-8"
        style={{ background: "var(--exhale-surface)", borderColor: "var(--exhale-border)" }}
        data-testid="profile-card"
      >
        <div className="flex items-center gap-4">
          <div
            className="w-16 h-16 rounded-full flex items-center justify-center font-mono text-xl font-bold ember-glow"
            style={{ background: "var(--exhale-badge)", color: "var(--exhale-accent-2)", border: "1px solid var(--exhale-border)" }}
            data-testid="profile-avatar"
          >
            {user.handle.slice(0, 2).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-medium tracking-widest uppercase mb-1" style={{ color: "var(--exhale-text-muted)" }}>
              Your anonymous handle
            </div>
            <h2 className="font-display text-2xl sm:text-3xl font-extrabold truncate" style={{ color: "var(--exhale-text)" }} data-testid="profile-handle">
              {user.handle}
            </h2>
          </div>
          <Button
            onClick={shuffleHandle}
            disabled={regenerating}
            data-testid="regenerate-handle-btn"
            variant="outline"
            className="rounded-full"
            style={{ borderColor: "var(--exhale-border)", background: "var(--exhale-bg)", color: "var(--exhale-text)" }}
          >
            <Shuffle className="w-4 h-4 mr-2" />
            {regenerating ? "…" : "Regenerate"}
          </Button>
        </div>
        <p className="mt-4 text-sm" style={{ color: "var(--exhale-text-2)" }}>
          No email. No password. No trace. Your identity refreshes with a click, and old vents keep the new mask.
        </p>
      </div>

      <Tabs defaultValue="vents">
        <TabsList
          className="rounded-full p-1 border"
          style={{ background: "var(--exhale-surface)", borderColor: "var(--exhale-border)" }}
        >
          <TabsTrigger value="vents" data-testid="tab-vents" className="rounded-full data-[state=active]:bg-[color:var(--exhale-accent)] data-[state=active]:text-[#0C0B0E]">
            Your vents ({posts.length})
          </TabsTrigger>
          <TabsTrigger value="bookmarks" data-testid="tab-bookmarks" className="rounded-full data-[state=active]:bg-[color:var(--exhale-accent)] data-[state=active]:text-[#0C0B0E]">
            <Bookmark className="w-3.5 h-3.5 mr-1.5" />
            Saved ({bookmarks.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="vents" className="mt-4">
          {posts.length === 0 ? (
            <div
              className="text-center py-12 rounded-2xl border"
              style={{ background: "var(--exhale-surface)", borderColor: "var(--exhale-border)" }}
              data-testid="profile-empty"
            >
              <p style={{ color: "var(--exhale-text-2)" }}>No vents yet. Head back and release something.</p>
            </div>
          ) : (
            <div className="space-y-4" data-testid="profile-vents">
              {posts.map((p) => (
                <VentCard key={p.id} post={p} user={user} onUpdate={updatePost} />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="bookmarks" className="mt-4">
          {bookmarks.length === 0 ? (
            <div
              className="text-center py-12 rounded-2xl border"
              style={{ background: "var(--exhale-surface)", borderColor: "var(--exhale-border)" }}
              data-testid="bookmarks-empty"
            >
              <p style={{ color: "var(--exhale-text-2)" }}>
                Nothing saved yet. Tap the menu on any vent to save it for later.
              </p>
            </div>
          ) : (
            <div className="space-y-4" data-testid="profile-bookmarks">
              {bookmarks.map((p) => (
                <VentCard key={p.id} post={p} user={user} onUpdate={updateBookmark} />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
